import { useState } from "react"
import { Control, useController } from "react-hook-form"
import * as z from "zod"
import { Button } from "~/ui/button"
import { Input } from "~/ui/input"
import { Label } from "~/ui/label"
import { useToast } from "~/ui/use-toast"
import { X } from "lucide-react"

interface LettermemeUsersFieldProps {
  control: Control<any>
  name: string
  label?: string
  ownerEmails?: string[]
}

const emailSchema = z.string().email()

export const LettermemeUsersField: React.FC<LettermemeUsersFieldProps> = ({
  control,
  name,
  label = "User Emails",
  ownerEmails = [],
}) => {
  const [inputValue, setInputValue] = useState("")
  const { toast } = useToast()
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
  })

  const emails: string[] = field.value || []
  const allEmails = [...ownerEmails, ...emails]

  const handleAdd = () => {
    const trimmedEmail = inputValue.trim()

    if (!trimmedEmail) return

    const result = emailSchema.safeParse(trimmedEmail)
    if (!result.success) {
      toast({
        title: "Invalid email",
        description: "Please enter a valid email address",
        variant: "destructive",
      })
      return
    }

    if (allEmails.includes(trimmedEmail)) {
      toast({
        title: "Duplicate email",
        description: "This email is already in the list",
        variant: "destructive",
      })
      return
    }

    field.onChange([...emails, trimmedEmail])
    setInputValue("")
  }

  const handleRemove = (emailToRemove: string) => {
    if (ownerEmails.includes(emailToRemove)) return
    field.onChange(emails.filter((email) => email !== emailToRemove))
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault()
      handleAdd()
    }
  }

  return (
    <div className="space-y-2">
      {label && <Label>{label}</Label>}

      <div className="flex gap-2">
        <Input
          type="email"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Enter email address"
        />
        <Button type="button" onClick={handleAdd} variant="outline">
          Add
        </Button>
      </div>

      {error && <p className="text-sm text-destructive">{error.message}</p>}

      {allEmails.length > 0 && (
        <div className="mt-4 space-y-2">
          {allEmails.map((email) => {
            const isReadonly = ownerEmails.includes(email)
            return (
              <div
                key={email}
                className="flex items-center justify-between gap-2 rounded-md border p-2"
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm">{email}</span>
                  {isReadonly && <span className="text-xs text-muted-foreground">(owner)</span>}
                </div>
                {!isReadonly && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemove(email)}
                    className="h-6 w-6 p-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
